/**
 * NFO FUTURES · EXCEL V2.5 — UI surface.
 *
 * ARCHITECTURE (corrected): the workbook has market-specific ANALYSIER formulas but ONE
 * instrument-agnostic DASHBOARD execution engine. So this module owns only the NFO
 * OPPORTUNITY surface (the linear spot-anchored Analysier model) and the legacy book.
 * NFO TRADES are rendered with the SAME shared components as MCX — TradeCard,
 * TradePlanModal, TradeConfigModal, AuditDrawer — because they are ordinary trade
 * instances on the shared engine, listed via /paper/v25/trades?market=NFO.
 *
 * Labelling rule: FORMULAS are Excel-literal, but lot size and days-to-expiry are taken LIVE
 * from the exchange, so this is FORMULA PARITY — never "workbook output parity".
 */
import React, { useEffect, useState } from "react";
import { ConvergeCell, MarginCell, ReturnCell, SCENARIO_TITLES, SpreadEntryCell } from "@/components/FuturesScenario";
import { useHeldRows, heldNote } from "@/lib/heldRows";
import { ECON_TITLE } from "@/lib/labels";
import { SpreadCell, addGate } from "@/components/OpportunityTradeCells";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { inr, num } from "@/lib/format";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { OpportunityInfo, ExactSymbol, OiCell, PairCell, ActiveTradeHint } from "@/components/OpportunityInfo";
import { filterOpportunities } from "@/lib/opportunityFilters";
import { PinnedOpportunities, PinToggle } from "@/components/PinnedOpportunities";
import { visibleInterval } from "@/lib/poll";
import RowStateBadge from "@/components/RowStateBadge";

export const NFO_STRATEGY_LABEL = "NFO FUTURES";

/** The current broker/exchange lot, and only that. Order quantity uses this lot; the old
 *  workbook value (`excel_lot`) is still returned by the API for parity/debugging but is no
 *  longer part of the production display. */
export function LotCell({ live }) {
  if (live == null) return <span className="text-slate-600">—</span>;
  return <span>{live}</span>;
}

// Default NFO columns. Spread (and H / I), DTE, Lot, Funds (L), Margin (M), Status, DIR, Why
// and the spot symbol live in the row's Info view.
const NFO_COLUMNS = [
  ["#", "text-left"], ["Underlying", "text-left"], ["Pair", "text-left"], ["SELL (exact)", "text-left"], ["BUY (exact)", "text-left"],
  ["Spread", "text-right"], ["Converge P&L", "text-right"], ["Margin", "text-right"], ["Return on margin", "text-right"],
  ["Spot LTP", "text-right"], ["Near LTP", "text-right"], ["Far LTP", "text-right"],
  ["OI NEAR", "text-right"], ["OI FAR", "text-right"], ["Bid/Ask", "text-right"], ["Add", "text-left"], ["Info", "text-left"],
];

export function FormulaParityBadge({ compact }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-sm bg-[#00C805]/15 text-[#00C805] font-bold"
      title="The Analysier NFO formulas are reproduced literally (H, I, J, L, M, N, O and the P/Q leg picker). Lot size and days-to-expiry are taken LIVE from the exchange, so values are not identical to the workbook's cached cells.">
      <ShieldCheck className="h-3 w-3" /> FORMULA PARITY: PASS
      {!compact && <span className="font-normal text-[#00C805]/70">· Excel-literal formulas · live market inputs</span>}
    </span>
  );
}

/** The one paragraph that must stay factually correct about what Excel does with NFO. */
export function NfoModelNote() {
  return (
    <div className="text-[10px] text-slate-600 leading-relaxed">
      Ranked by return on margin. The near and far futures are traded; the spot price is only an input.
      <details className="mt-1"><summary className="cursor-pointer text-slate-500">How it is calculated</summary>
      <span className="text-slate-400 font-semibold">Opportunity model (NFO-specific):</span> Excel Analysier
      linear, spot-anchored — H = (Bfar−Bspot)·K/50 + (Bspot−Bnear), I = −H, J = MAX(I,H)·G,
      L = Bspot·G·22.5%, M = L·20%, N = J/M, O = N/K. Spot is a price input only and is never traded.
      {" "}
      <span className="text-slate-400 font-semibold">Execution engine (shared with MCX):</span> the workbook's
      single Dashboard engine — K1 edge gate, 10-level averaging ladder, Excel lot progression (1, 2, 6, 13 …),
      funds gate, Far-First order sequencing, G24 profit target, auto and manual square-off. Proven from the
      workbook: Analysier AA2:AN82 ranks MCX and NFO in one list, and the cached Dashboard blocks were running
      NFO contracts (WIPRO / TATASTEEL / DABUR), with DABUR filled to level 3 at lots 1 → 2 → 6.
      </details>
    </div>
  );
}

/* ------------------------------------------------------------------ Opportunities table */
// `pins`: useOpportunityPins() from the page (optional). Pinned NFO rows render above the table
// from the same poll, using the FULL row list (`d.rows`) so a stale pinned pair is still found.
export function NfoOpportunities({ onAssign, filters, pins = null }) {
  const [live, setD] = useState(null);
  const held = useHeldRows(live);           // W3: the table holds still under the pointer
  const d = held.rows;
  const { systemStatus } = useApp() || {};
  const nfoOpen = systemStatus?.market?.exchanges?.NFO?.open;      // undefined until the status loads
  useEffect(() => {
    const load = () => api.get("/paper/nfo/opportunities").then((r) => setD(r.data)).catch(() => {});
    load(); return visibleInterval(load, 3000);
  }, []);

  if (!d) return <div className="term-panel rounded-sm p-8 text-center text-sm text-slate-500">Loading NFO opportunities…</div>;
  if (!d.zerodha_ready) {
    return (
      <div className="term-panel rounded-sm p-8 text-center" data-testid="nfo-waiting">
        <AlertTriangle className="h-8 w-8 text-[#FFCC00] mx-auto mb-3" />
        <div className="text-sm font-semibold">NFO FUTURES · WAITING FOR ZERODHA</div>
        <div className="text-xs text-slate-500 mt-1">{NFO_STRATEGY_LABEL} runs on live NFO data only — no synthetic fallback.</div>
      </div>
    );
  }

  // Rows arrive ranked by %Profit (N) descending; the filters only hide rows.
  const shown = filterOpportunities(d.opportunities, filters);

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-2 text-[11px] mono">
        <div className="flex items-center gap-2">
          <FormulaParityBadge />
          <span className="text-slate-500" data-testid="nfo-opp-count">{shown.length} of {d.opportunities.length} ranked shown · universe {d.universe_count} · resolved {d.resolved_count}</span>
        </div>
        <span className="text-slate-500" title={d.ranking_key}>Ranked by net convergence P&L / Zerodha margin</span>
      </div>

      {pins && <PinnedOpportunities market="NFO" pins={pins.pins} rows={d.rows || d.opportunities}
        onTrade={onAssign} onUnpin={(id) => pins.unpin(id).catch(() => {})} />}

      <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 320px)" }} {...held.bind}>
        {held.held && <div className="text-[10px] px-2 py-0.5 bg-[#b38f00]/20 text-[#b38f00]" data-testid="nfo-opp-held">{heldNote(held)}</div>}
        <table className="w-full text-xs border-collapse" data-testid="nfo-opp-table">
          <thead className="sticky top-0 z-10 bg-[#1A1D24] text-slate-400">
            <tr>{NFO_COLUMNS.map(([h, alignCls]) => (
              <th key={h} title={SCENARIO_TITLES[h] || ECON_TITLE[h]} className={`px-1.5 py-2 whitespace-nowrap font-semibold ${alignCls}`}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={NFO_COLUMNS.length} className="px-4 py-6 text-center text-slate-600">
                {d.opportunities.length === 0 ? "Waiting for live ticks / no qualifying NFO opportunities."
                  : "No NFO opportunities match the current Profit % Min / OI Min filters."}
              </td></tr>
            )}
            {shown.map((r) => {
              const rid = r.pair_selection === "MID_FAR" ? `${r.name}-MID_FAR` : r.name;
              const active = (d.active_underlyings || []).filter((a) => a.underlying === r.name && a.market_segment === "NFO");
              const gate = addGate(r, { sessionOpen: nfoOpen });
              return (
              <tr key={r.opportunity_id || r.name} className="gz-row mono" data-testid={`nfo-opp-row-${rid}`}>
                <td className="px-1.5 py-1.5 text-left text-slate-500">{r.rank}</td>
                <td className="px-1.5 py-1.5 text-left font-bold text-slate-100 whitespace-nowrap">{r.name}</td>
                <td className="px-1.5 py-1.5 text-left"><PairCell row={r} testId={`nfo-pair-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-left max-w-[9rem]"><ExactSymbol symbol={r.sell_symbol} cls="text-[#FF6b62]" testId={`nfo-sell-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-left max-w-[9rem]"><ExactSymbol symbol={r.buy_symbol} cls="text-[#5b9bff]" testId={`nfo-buy-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><SpreadEntryCell row={r} testId={`nfo-entry-spread-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><ConvergeCell row={r} testId={`nfo-converge-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><MarginCell row={r} testId={`nfo-margin-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><ReturnCell row={r} testId={`nfo-roc-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-right text-slate-400 whitespace-nowrap">{num(r.spot_ltp, 2)}</td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap">{num(r.near_ltp, 2)}</td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap">{num(r.far_ltp, 2)}</td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><OiCell value={r.near_oi} /></td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><OiCell value={r.far_oi} /></td>
                <td className="px-1.5 py-1.5 text-right whitespace-nowrap"><SpreadCell row={r} testId={`nfo-spread-${rid}`} /></td>
                <td className="px-1.5 py-1.5 text-left">
                  {pins && <PinToggle row={r} market="NFO" pinFor={pins.pinFor} pin={pins.pin} unpin={pins.unpin} />}
                  <button disabled={gate.disabled} title={gate.title} onClick={() => onAssign(r)} data-testid={`nfo-add-trade-${rid}`}
                    className="text-[10px] font-bold px-2 py-1 rounded-sm bg-[#2563EB] whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">{gate.label}</button>
                  <RowStateBadge state={r.state} testId={`nfo-state-${rid}`} />
                  <ActiveTradeHint active={active} testId={`nfo-active-${rid}`} />
                </td>
                <td className="px-1.5 py-1.5 text-left"><OpportunityInfo row={r} market="NFO" testId={`nfo-info-${rid}`} /></td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NfoModelNote />
    </>
  );
}

/* NFO trades are rendered by the unified Paper Trading list (TradeCard), and the legacy
 * NFO_SIMPLE book has been retired — this module is now the NFO OPPORTUNITY surface only. */
